// ─── Google Analytics (gtag.js) – consent-based loader ─────────────────────
//
// gtag is loaded dynamically ONLY after the user accepts cookies.
// Consent state is stored in localStorage under 'dicepit_cookieConsent'.

const GA_MEASUREMENT_ID = 'G-68ZKVHT554';
const CONSENT_KEY = 'dicepit_cookieConsent';

export type ConsentStatus = 'accepted' | 'declined' | null;

/** Read the stored consent preference. */
export function getConsent(): ConsentStatus {
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === 'accepted' || value === 'declined') return value;
  return null;
}

/** Persist the user's consent choice and act on it. */
export function setConsent(status: 'accepted' | 'declined'): void {
  localStorage.setItem(CONSENT_KEY, status);
  if (status === 'accepted') {
    loadGtag();
  }
}

/** Inject the gtag.js script into the page (idempotent). */
export function loadGtag(): void {
  // Prevent double-loading
  if (document.getElementById('gtag-script')) return;

  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialise dataLayer + gtag function
  // Must use `arguments` (not rest params) so gtag.js receives the raw Arguments object
  const w = window as unknown as { dataLayer: IArguments[]; gtag: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  w.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments as unknown as IArguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', GA_MEASUREMENT_ID);
}

/**
 * Call once on app boot – if the user already accepted before, load gtag
 * immediately so returning visitors are tracked without seeing the banner again.
 */
export function initAnalytics(): void {
  if (getConsent() === 'accepted') {
    loadGtag();
  }
}

// ─── Custom event tracking ──────────────────────────────────────────────────

/** Send a custom event to GA4. Silently no-ops if gtag isn't loaded. */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag === 'function') {
    w.gtag('event', eventName, params);
  }
}
